import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { TierBadge, formatScore } from "../components/TierBadge";
import { ArrowLeft, EnvelopeSimple, Eye, CursorClick, WarningCircle, PaperPlaneTilt, FloppyDisk, PencilSimple, X, LinkedinLogo, Globe, MagicWand, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const EVENT_ICON = {
  sent: PaperPlaneTilt,
  open: Eye,
  click: CursorClick,
  bounce: WarningCircle,
  unsubscribe: WarningCircle,
};

export default function ContactDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [tl, setTl] = useState([]);
  const [edit, setEdit] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scoreInfo, setScoreInfo] = useState(null);

  useEffect(() => {
    api.getContact(id).then(d => { setC(d); setEdit(d); });
    api.contactTimeline(id).then(d => setTl(d.items || []));
    api.scoreBreakdown(id).then(setScoreInfo).catch(() => {});
  }, [id]);

  if (!c) return <div className="text-sm text-zinc-500">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      const upd = await api.updateContact(id, {
        email: edit.email, first_name: edit.first_name, last_name: edit.last_name, phone: edit.phone,
        company: edit.company, job_title: edit.job_title, lifecycle_stage: edit.lifecycle_stage,
        website: edit.website, linkedin_url: edit.linkedin_url,
        source: edit.source, tags: edit.tags, notes: edit.notes, status: edit.status,
      });
      setC(upd);
      setEdit(upd);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const cancelEdit = () => { setEdit(c); setEditing(false); };

  const reviewLk = async (action) => {
    try {
      const upd = await api.linkedinReview(id, action);
      setC(upd);
      setEdit(upd);
      toast.success(action === "confirm" ? "Marked as correct" : "Link removed — it can be re-enriched later");
    } catch {
      toast.error("Could not update");
    }
  };

  const enrichNow = async () => {
    setBusy(true);
    try {
      const r = await api.enrichContact(id);
      setC(r.contact);
      setEdit(r.contact);
      if (r.filled?.length) {
        toast.success(`Enriched — filled ${r.filled.map(f => f === "job_title" ? "job title" : "LinkedIn").join(" and ")}`);
      } else if (r.found) {
        toast.info("Hunter found this person but had nothing new to add");
      } else {
        toast.info("Hunter has no profile for this email");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrichment failed");
    } finally { setBusy(false); }
  };

  const verifyNow = async () => {
    setBusy(true);
    try {
      const r = await api.verifyContact(id);
      setC(r.contact);
      setEdit(r.contact);
      toast.success(`Email is ${r.verification.bucket}${r.verification.score != null ? ` (score ${r.verification.score})` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const markDeliverable = async () => {
    setBusy(true);
    try {
      const r = await api.markDeliverable(id);
      setC(r.contact);
      setEdit(r.contact);
      toast.success("Marked as deliverable — bounce suppression lifted and contact is mailable again");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Link to="/contacts" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"><ArrowLeft size={14}/> Back to contacts</Link>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">{c.first_name} {c.last_name}</h2>
          <div className="text-sm text-zinc-600 flex items-center gap-1.5 mt-1"><EnvelopeSimple size={14}/> {c.email}</div>
        </div>
        <div className="flex gap-2 items-center">
          <StatusBadge value={c.status}/>
          {editing ? (
            <>
              <button data-testid="cancel-edit-contact" onClick={cancelEdit} disabled={busy} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50"><X size={14}/> Cancel</button>
              <button data-testid="save-contact" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40"><FloppyDisk size={14}/> {busy ? "Saving…" : "Save"}</button>
            </>
          ) : (
            <>
              {(!c.job_title || !c.linkedin_url) && (
                <button data-testid="enrich-contact" onClick={enrichNow} disabled={busy} title="Fill missing job title / LinkedIn from Hunter" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-40"><MagicWand size={14}/> {busy ? "Working…" : "Enrich"}</button>
              )}
              <button data-testid="edit-contact" onClick={() => { setEdit(c); setEditing(true); }} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><PencilSimple size={14}/> Edit</button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <h3 className="font-heading text-base font-medium mb-4">Details</h3>
            {!editing ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-3" data-testid="details-readonly">
                <RO label="First name" value={c.first_name} testid="ro-first"/>
                <RO label="Last name" value={c.last_name} testid="ro-last"/>
                <RO label="Email" value={c.email} testid="ro-email" mono/>
                <RO label="Company" value={c.company} testid="ro-company"/>
                <RO label="Job title" value={c.job_title} testid="ro-title"/>
                <RO label="Phone" value={c.phone} testid="ro-phone"/>
                <div>
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Website</span>
                  {c.website ? (
                    <a data-testid="ro-website" href={/^https?:\/\//.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1.5"><Globe size={14}/>{c.website}</a>
                  ) : <span className="text-sm text-zinc-300">—</span>}
                </div>
                <div>
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">LinkedIn</span>
                  {c.linkedin_url ? (
                    <>
                      <a data-testid="ro-linkedin" href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1.5"><LinkedinLogo size={14} weight="fill"/>View profile</a>
                      {c.linkedin_flag === "review" && (
                        <div data-testid="ro-linkedin-review" className="mt-1">
                          <p className="text-[11px] text-amber-600">Name doesn't appear in this URL — worth double-checking it's the right profile</p>
                          <div className="flex gap-2 mt-1.5">
                            <button data-testid="ro-linkedin-confirm" onClick={() => reviewLk("confirm")} className="text-[11px] px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50">✓ Confirm it's correct</button>
                            <button data-testid="ro-linkedin-wrong" onClick={() => reviewLk("wrong")} className="text-[11px] px-2 py-1 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50">✕ Wrong profile — remove link</button>
                          </div>
                        </div>
                      )}
                      {c.linkedin_flag === "confirmed" && (
                        <p className="text-[11px] text-emerald-600 mt-0.5" data-testid="ro-linkedin-confirmed">Profile manually confirmed</p>
                      )}
                    </>
                  ) : <span className="text-sm text-zinc-300">—</span>}
                </div>
                <div>
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Stage</span>
                  <StatusBadge value={c.lifecycle_stage}/>
                </div>
                <div>
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Status</span>
                  <div className="flex items-center gap-2">
                    <StatusBadge value={c.status}/>
                    {c.status === "left_company" && c.left_company_at && (
                      <span className="text-[11px] text-zinc-400" data-testid="ro-left-date">since {new Date(c.left_company_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    )}
                  </div>
                </div>
                <RO label="Source" value={c.source} testid="ro-source"/>
                <RO label="Email preference" value={c.email_frequency === "paused" ? "Paused" : c.email_frequency === "monthly" ? "Monthly digest" : "All emails"} testid="ro-frequency"/>
                <div className="col-span-2">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Email deliverability</span>
                  {c.email_verification ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid="ro-verification" className={`text-[11px] font-medium px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${
                        c.email_verification.bucket === "deliverable" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : c.email_verification.bucket === "risky" ? "border-amber-200 bg-amber-50 text-amber-700"
                        : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                        <SealCheck size={12} weight="fill"/> {c.email_verification.bucket}{c.email_verification.score != null ? ` · score ${c.email_verification.score}` : ""}
                      </span>
                      <span className="text-[11px] text-zinc-400" data-testid="ro-verification-source">
                        {c.email_verification.source === "bounce" ? "via a bounced send"
                          : c.email_verification.source === "manual" ? "manual override"
                          : c.email_verification.source === "delivery" ? "via a successful delivery"
                          : "via Hunter check"}
                        {" · checked "}{new Date(c.email_verification.checked_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      <button data-testid="reverify-contact" onClick={verifyNow} disabled={busy} className="text-[11px] text-zinc-500 underline underline-offset-2 hover:text-zinc-900 disabled:opacity-40">Re-check (0.5 credits)</button>
                    </div>
                  ) : (
                    <button data-testid="verify-contact-btn" onClick={verifyNow} disabled={busy} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40">
                      <SealCheck size={12}/> Verify with Hunter (0.5 credits)
                    </button>
                  )}
                  {(c.email_verification?.bucket === "undeliverable" || c.status === "bounced") && (
                    <div className="mt-1.5">
                      <button data-testid="mark-deliverable-btn" onClick={markDeliverable} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                        <SealCheck size={12}/> Mark as deliverable — I know this address works
                      </button>
                      <p className="text-[11px] text-zinc-400 mt-1">Clears the undeliverable flag, lifts the bounce suppression and makes the contact mailable again.</p>
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Tags</span>
                  {(c.tags || []).length ? (
                    <div className="flex flex-wrap gap-1" data-testid="ro-tags">
                      {c.tags.map(t => <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">{t}</span>)}
                    </div>
                  ) : <span className="text-sm text-zinc-300">—</span>}
                </div>
                <div className="col-span-2">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Notes</span>
                  <p data-testid="ro-notes" className="text-sm text-zinc-700 whitespace-pre-wrap">{c.notes || <span className="text-zinc-300">—</span>}</p>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="details-edit">
              <Input label="First name" value={edit.first_name||""} onChange={v => setEdit({...edit, first_name: v})} testid="d-first"/>
              <Input label="Last name" value={edit.last_name||""} onChange={v => setEdit({...edit, last_name: v})} testid="d-last"/>
              <label className="block col-span-2">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Email</span>
                <input data-testid="d-email" value={edit.email||""} onChange={(e) => setEdit({...edit, email: e.target.value})} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"/>
              </label>
              <Input label="Company" value={edit.company||""} onChange={v => setEdit({...edit, company: v})} testid="d-company"/>
              <Input label="Job title" value={edit.job_title||""} onChange={v => setEdit({...edit, job_title: v})} testid="d-title"/>
              <Input label="Phone" value={edit.phone||""} onChange={v => setEdit({...edit, phone: v})} testid="d-phone"/>
              <Input label="Source" value={edit.source||""} onChange={v => setEdit({...edit, source: v})} testid="d-source"/>
              <label className="block">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Website</span>
                <div className="flex gap-1.5">
                  <input data-testid="d-website" value={edit.website||""} onChange={(e) => setEdit({...edit, website: e.target.value})} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"/>
                  {edit.website && <a data-testid="d-website-open" href={/^https?:\/\//.test(edit.website) ? edit.website : `https://${edit.website}`} target="_blank" rel="noreferrer" className="h-9 px-2.5 inline-flex items-center rounded-md border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 shrink-0">Open</a>}
                </div>
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">LinkedIn URL</span>
                <div className="flex gap-1.5">
                  <input data-testid="d-linkedin" value={edit.linkedin_url||""} onChange={(e) => setEdit({...edit, linkedin_url: e.target.value})} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"/>
                  {edit.linkedin_url && <a data-testid="d-linkedin-open" href={edit.linkedin_url} target="_blank" rel="noreferrer" className="h-9 px-2.5 inline-flex items-center rounded-md border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 shrink-0">Open</a>}
                </div>
              </label>
              <Select label="Stage" value={edit.lifecycle_stage||"lead"} onChange={v => setEdit({...edit, lifecycle_stage: v})} options={["lead","qualified","opportunity","customer"]} testid="d-stage"/>
              <label className="block">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Status</span>
                <select data-testid="d-status" value={edit.status||"active"} onChange={(e) => setEdit({...edit, status: e.target.value})} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                  <option value="active">active</option>
                  <option value="left_company">no longer at company</option>
                  <option value="unsubscribed">unsubscribed</option>
                  <option value="bounced">bounced</option>
                </select>
                {edit.status === "left_company" && <p className="text-[11px] text-orange-600 mt-1">Excluded from every campaign and sequence until set back to active.</p>}
              </label>
              <div className="col-span-2">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Tags (comma separated)</span>
                <input data-testid="d-tags" value={(edit.tags||[]).join(", ")} onChange={(e) => setEdit({...edit, tags: e.target.value.split(",").map(t=>t.trim()).filter(Boolean)})} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
              </div>
              <div className="col-span-2">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Notes</span>
                <textarea data-testid="d-notes" rows={3} value={edit.notes||""} onChange={(e) => setEdit({...edit, notes: e.target.value})} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"/>
              </div>
            </div>
            )}
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <h3 className="font-heading text-base font-medium mb-4">Engagement timeline</h3>
            {tl.length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded">No activity yet</div>
            ) : (
              <ol className="relative border-l-2 border-zinc-100 pl-5 space-y-3">
                {tl.map(ev => {
                  const Icon = EVENT_ICON[ev.type] || PaperPlaneTilt;
                  return (
                    <li key={ev.id} className="relative">
                      <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-white border-2 border-zinc-300" />
                      <div className="flex items-center gap-2 text-sm">
                        <Icon size={14} className="text-zinc-500"/>
                        <span className="font-medium capitalize">{ev.type === "followup" ? "Follow-up email" : ev.type}</span>
                        {ev.subject && <span className="text-xs text-zinc-500 truncate max-w-[260px]">"{ev.subject}"</span>}
                        {ev.url && <a href={ev.url} className="text-xs text-zinc-500 truncate max-w-[260px]" target="_blank" rel="noreferrer">{ev.url}</a>}
                        <span className="text-xs text-zinc-400 ml-auto">{new Date(ev.created_at).toLocaleString()}</span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="lead-score-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-base font-medium">Lead score</h3>
              <TierBadge score={c.engagement_score || 0} testid="lead-tier-badge"/>
            </div>
            <div className="font-heading text-3xl font-medium tracking-tight text-zinc-950" data-testid="lead-score-value">{formatScore(c.engagement_score)}</div>
            {scoreInfo && (
              <div className="mt-3 space-y-0">
                <Stat label={`Opens (${scoreInfo.parts.open.count})`} value={`+${scoreInfo.parts.open.points} pts`}/>
                <Stat label={`Clicks (${scoreInfo.parts.click.count})`} value={`+${scoreInfo.parts.click.points} pts`}/>
                <Stat label={`Publication requests (${scoreInfo.parts.request.count})`} value={`+${scoreInfo.parts.request.points} pts`}/>
                <Stat label="Last activity" value={scoreInfo.last_activity_at ? new Date(scoreInfo.last_activity_at).toLocaleDateString() : "—"}/>
              </div>
            )}
            <p className="text-[11px] text-zinc-400 mt-3">Recent activity counts most — points halve every {scoreInfo?.half_life_days || 45} days. Hot ≥ 15 · Warm ≥ 5.</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <h3 className="font-heading text-base font-medium mb-4">Stats</h3>
            <Stat label="Total sent" value={c.total_sent || 0}/>
            <Stat label="Opens" value={c.total_opens || 0}/>
            <Stat label="Clicks" value={c.total_clicks || 0}/>
            <Stat label="Last opened" value={c.last_opened_at ? new Date(c.last_opened_at).toLocaleDateString() : "—"}/>
            <Stat label="Created" value={new Date(c.created_at).toLocaleDateString()}/>
          </div>
        </div>
      </div>
    </div>
  );
}

const RO = ({ label, value, testid, mono }) => (
  <div>
    <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">{label}</span>
    <span data-testid={testid} className={`text-sm text-zinc-900 ${mono ? "font-mono text-[12.5px]" : ""}`}>{value || <span className="text-zinc-300">—</span>}</span>
  </div>
);

const Input = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">{label}</span>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950"/>
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">{label}</span>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const Stat = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className="font-medium text-zinc-900">{value}</span>
  </div>
);
